// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
} from '@mui/material';
import { BiSearch, BiX } from 'react-icons/bi';
import Icon from '@mui/material/Icon';

import { useGetPortalFeaturesQuery } from '../queries/getPortalFeatures.query.generated';
import ReusableMultiselect from '@components/Selectors/ReusableMultiselect';

// TYPES ---------------------------------------------------------------- //

export interface TeamControlFilters {
  features: string[];
  status: string | null;
  search: string;
}

interface TeamControlsProps {
  filters: TeamControlFilters;
  onFiltersChange: (filters: TeamControlFilters) => void;
  /** Hide the feature multiselect (e.g. on Drivers tab) */
  hideFeatures?: boolean;
}

// CONSTANTS ---------------------------------------------------------------- //

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// COMPONENT ---------------------------------------------------------------- //

const TeamControls: React.FC<TeamControlsProps> = ({ filters, onFiltersChange, hideFeatures }) => {
  const [searchInput, setSearchInput] = useState(filters.search);

  const { data: featuresData } = useGetPortalFeaturesQuery({
    fetchPolicy: 'cache-first',
    skip: hideFeatures,
  });

  const featureOptions = React.useMemo(
    () => (featuresData?.features ?? []).map(f => ({ id: f.name, name: f.label })),
    [featuresData],
  );

  const selectedFeatures = React.useMemo(
    () => filters.features.map(name => {
      const opt = featureOptions.find(o => o.id === name);
      return { id: name, name: opt?.name ?? name };
    }),
    [filters.features, featureOptions],
  );

  const handleSearchCommit = () => {
    onFiltersChange({ ...filters, search: searchInput.trim() });
  };

  const handleClearSearch = () => {
    setSearchInput('');
    onFiltersChange({ ...filters, search: '' });
  };

  const handleFeatureChange = (_label: string | null, items?: { id: string | null | undefined }[]) => {
    const names = (items ?? []).map(i => i.id).filter(Boolean) as string[];
    onFiltersChange({ ...filters, features: names });
  };

  return (
    <Box display="flex" alignItems="center" gap={1.5} width="100%">
      {/* Feature multiselect */}
      {!hideFeatures && (
        <Box sx={{ minWidth: 200, maxWidth: 300 }}>
          <ReusableMultiselect
            label="Filter By Feature"
            dataArray={featureOptions}
            selected={selectedFeatures}
            handleChange={handleFeatureChange}
          />
        </Box>
      )}

      {/* Status dropdown */}
      <TextField
        select
        size="small"
        label="Status"
        value={filters.status ?? 'all'}
        onChange={e => onFiltersChange({ ...filters, status: e.target.value === 'all' ? null : e.target.value })}
        sx={{ minWidth: 120 }}
      >
        {STATUS_OPTIONS.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>

      {/* Search field (enter-to-commit) */}
      <TextField
        fullWidth
        size="small"
        label="Search (Press Enter)"
        placeholder="Search by name, email, or phone..."
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleSearchCommit()}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <BiSearch size={18} color="#9ca3af" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                {searchInput !== filters.search ? (
                  <IconButton size="small" onClick={handleSearchCommit} title="Search">
                    <Icon fontSize="small">arrow_forward</Icon>
                  </IconButton>
                ) : (
                  <IconButton size="small" onClick={handleClearSearch} title="Clear search">
                    <BiX size={18} />
                  </IconButton>
                )}
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default TeamControls;
